import { useAtomValue, useSetAtom } from 'jotai';

import {
	type AppliedFilterItem,
	FiltersDrawerAppliedFiltersSection,
} from '@common/components/FiltersDrawer';

import { allAppliedFiltersAtom } from '../../atoms/ordersAppliedFiltersAtoms';
import {
	clearAllFiltersAtom,
	financingStatusFilterAtom,
	modelFilterAtom,
	paymentChoiceFilterAtom,
	paymentStatusFilterAtom,
	typeFilterAtom,
	updateActionRequiredFilterAtom,
	updateFinancingStatusFilterAtom,
	updateFulfillmentDateFilterAtom,
	updateFulfillmentTypeFilterAtom,
	updateModelFilterAtom,
	updateOrderDateFilterAtom,
	updatePaymentChoiceFilterAtom,
	updatePaymentStatusFilterAtom,
	updateRegistrationStateFilterAtom,
	updateSearchValueAtom,
	updateTypeFilterAtom,
} from '../../atoms/ordersAtoms';

/**
 * OrdersFiltersDrawerAppliedFiltersSection component that displays currently applied filters
 * with the ability to remove individual filters or clear all filters
 */
export function OrdersFiltersDrawerAppliedFiltersSection() {
	const allAppliedFilters = useAtomValue(allAppliedFiltersAtom);
	const clearAllFilters = useSetAtom(clearAllFiltersAtom);
	const setSearchValue = useSetAtom(updateSearchValueAtom);
	const setActionRequiredFilter = useSetAtom(updateActionRequiredFilterAtom);
	const setPaymentChoices = useSetAtom(updatePaymentChoiceFilterAtom);
	const setPaymentStatuses = useSetAtom(updatePaymentStatusFilterAtom);
	const setFinancingStatuses = useSetAtom(updateFinancingStatusFilterAtom);
	const setModels = useSetAtom(updateModelFilterAtom);
	const setTypes = useSetAtom(updateTypeFilterAtom);
	const setRegistrationState = useSetAtom(updateRegistrationStateFilterAtom);
	const setOrderDateFilter = useSetAtom(updateOrderDateFilterAtom);
	const setFulfillmentType = useSetAtom(updateFulfillmentTypeFilterAtom);
	const setFulfillmentDateFilter = useSetAtom(updateFulfillmentDateFilterAtom);

	// Read current filter values for removal operations
	const currentPaymentChoices = useAtomValue(paymentChoiceFilterAtom);
	const currentPaymentStatuses = useAtomValue(paymentStatusFilterAtom);
	const currentFinancingStatuses = useAtomValue(financingStatusFilterAtom);
	const currentModels = useAtomValue(modelFilterAtom);
	const currentTypes = useAtomValue(typeFilterAtom);

	// Use the applied filters from the atoms with clear functions
	const allFilters: AppliedFilterItem[] = allAppliedFilters.map((filter) => ({
		...filter,
		clearFunction: getClearFunctionForFilter(filter.id),
	}));

	function getClearFunctionForFilter(filterId: string) {
		// Handle payment choice filters - extract the value from the id
		if (filterId.startsWith('payment-choice-')) {
			const value = filterId.replace('payment-choice-', '');
			return () => {
				const updatedChoices = currentPaymentChoices.filter((v) => v !== value);
				setPaymentChoices(updatedChoices);
			};
		}

		// Handle payment status filters - extract the value from the id
		if (filterId.startsWith('payment-status-')) {
			const value = filterId.replace('payment-status-', '');
			return () => {
				const updatedStatuses = currentPaymentStatuses.filter((v) => v !== value);
				setPaymentStatuses(updatedStatuses);
			};
		}

		// Handle financing status filters - extract the value from the id
		if (filterId.startsWith('financing-status-')) {
			const value = filterId.replace('financing-status-', '');
			return () => {
				const updatedStatuses = currentFinancingStatuses.filter((v) => v !== value);
				setFinancingStatuses(updatedStatuses);
			};
		}

		// Handle model filters - extract the value from the id
		if (filterId.startsWith('model-')) {
			const value = filterId.replace('model-', '');
			return () => {
				const updatedModels = currentModels.filter((v) => v !== value);
				setModels(updatedModels);
			};
		}

		// Handle type filters - extract the value from the id
		if (filterId.startsWith('type-')) {
			const value = filterId.replace('type-', '');
			return () => {
				const updatedTypes = currentTypes.filter((v) => v !== value);
				setTypes(updatedTypes);
			};
		}

		// Handle single-value filters
		switch (filterId) {
			case 'search-query':
				return () => setSearchValue('');
			case 'action-required':
				return () => setActionRequiredFilter(false);
			case 'registration-state':
				return () => setRegistrationState(null);
			case 'order-date':
				return () => setOrderDateFilter(null);
			case 'fulfillment':
				return () => {
					setFulfillmentType(null);
					setFulfillmentDateFilter(null);
				};
			default:
				return () => {};
		}
	}

	const handleRemoveFilter = (filterId: string) => {
		const filter = allFilters.find((f) => f.id === filterId);
		if (filter) {
			filter.clearFunction();
		}
	};

	return (
		<FiltersDrawerAppliedFiltersSection
			allAppliedFilters={allFilters}
			clearAllFilters={clearAllFilters}
			onRemoveFilter={handleRemoveFilter}
		/>
	);
}
